<template>
  <!-- CP-274: shown when a lazy chunk from an older deploy is gone. Refresh is user-initiated only.
       It never moves focus: role=status announces it; the Refresh ring shows on keyboard focus only. -->
  <div :class="['stale-chunk-note', `stale-chunk-note--${variant}`, { 'stale-chunk-note--square': square }]" role="status" aria-live="polite">
    <p class="stale-chunk-note__text">{{ text }}</p>
    <button type="button" class="stale-chunk-note__button" @click="refresh">{{ buttonText }}</button>
  </div>
</template>

<script>
import { NOTE_COPY } from '@/utils/chunkReload';

export default {
  name: 'StaleChunkNote',
  props: {
    // 'section': inside a chart / map box (absolutely fills it, so the box keeps its size).
    // 'page': in the main area after a route chunk failed twice within the guard window.
    variant: { type: String, default: 'section' },
    // 'page': the URL the user was navigating to; Refresh goes straight there.
    url: { type: String, default: '' },
    // LineChart: its wrapper has no height of its own; Chart.js sizes the canvas width x width (square). The note
    // takes that same square, so the page lays out exactly as it would with the chart (no shift either way).
    square: { type: Boolean, default: false }
  },
  computed: {
    text() { return this.variant === 'page' ? NOTE_COPY.page : NOTE_COPY.section; },
    buttonText() { return NOTE_COPY.button; }
  },
  methods: {
    refresh() {
      if (this.url) window.location.assign(this.url);
      else window.location.reload();
    }
  }
};
</script>

<style scoped>
.stale-chunk-note {
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  justify-content: center;
  gap: 8px 12px;
  box-sizing: border-box;
  padding: 12px 16px;
  text-align: center;
  color: var(--cs-d-text);
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
  font-size: 14px;
  line-height: 20px;
}

/* Fills the reserved chart / map box without changing its size (no layout shift). Stacked at every width:
   text, then the button centred below (UI/UX #215). */
.stale-chunk-note--section {
  position: absolute;
  inset: 0;
  z-index: 1;
  flex-direction: column;
  flex-wrap: nowrap;
}

.stale-chunk-note--section.stale-chunk-note--square {
  position: relative;
  inset: auto;
  width: 100%;
  aspect-ratio: 1 / 1;
}

.stale-chunk-note--page {
  max-width: 640px;
  margin: 24px auto;
  border: 1px solid var(--cs-d-border);
}

.stale-chunk-note__text {
  margin: 0;
  text-wrap: balance; /* UI/UX #215 follow-up: no lone "version." on the last line at 390 */
}

.stale-chunk-note__button {
  min-height: 44px;
  min-width: 44px;
  padding: 0 20px;
  background-color: var(--cs-fill);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.stale-chunk-note__button:hover {
  background-color: var(--cs-fill-hover);
}

/* Ring on keyboard focus only (:focus-visible), never at rest, on click or on a script's focus(). */
.stale-chunk-note__button:focus:not(:focus-visible) {
  outline: none;
}

.stale-chunk-note__button:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
}
</style>
